import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Grid, IconButton, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TRIAL_RESULT_LABELS } from "../../ProgramLibrary/shared/labels";

/** Prompt-level chip row. Stores id + name snapshot on next entry.
 *  Batch 36B — `loading` MUST be passed by the caller. During the
 *  hierarchy fetch this component renders a placeholder row instead of
 *  the "no prompt hierarchy" warning, which was previously a false
 *  clinical statement asserted before the lookup returned.
 */
export function PromptLevelSelector({ levels, value, onChange, loading }: {
  levels: Array<{ id: number; levelName: string; colour?: string }>;
  value: any; onChange: (v: any) => void;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
        {[0,1,2,3,4].map((i) => (
          <Chip key={i} size="small" label="…" variant="outlined" sx={{ opacity: 0.4, minWidth: 56 }} />
        ))}
      </Stack>
    );
  }
  if (!levels?.length) {
    return <Alert severity="warning" sx={{ mb: 1 }}>No prompt level template assigned to this target — new entries will not include a prompt level.</Alert>;
  }
  // Batch 13 Part C Step 4 — a selected level keeps ITS OWN configured
  // colour instead of collapsing to MUI primary blue. Kerthiga picked
  // those colours in the modal; the live-session buttons are what a
  // therapist looks at. Unselected: outlined in the level's colour.
  // Selected: solid fill in the level's colour + white ink. Levels
  // without a stored colour degrade to neutral outline (no invented hue).
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
      {levels.map((l) => {
        const c = l.colour && /^#([0-9a-f]{3}){1,2}$/i.test(l.colour) ? l.colour : null;
        const selected = value?.id === l.id;
        return (
          <Chip
            key={l.id} size="small" label={l.levelName}
            onClick={() => onChange({ id: l.id, name: l.levelName })}
            sx={c ? {
              borderColor: c,
              backgroundColor: selected ? c : "transparent",
              color: selected ? "#ffffff" : c,
              fontWeight: selected ? 700 : 500,
              border: `1px solid ${c}`,
              "&:hover": { backgroundColor: selected ? c : `${c}22` },
            } : {
              // No stored colour — outline with divider, no invented hue.
              border: `1px solid`,
              borderColor: "divider",
              backgroundColor: selected ? "action.selected" : "transparent",
            }}
          />
        );
      })}
      <Chip size="small" label="No prompt" onClick={() => onChange(null)}
        variant={!value ? "filled" : "outlined"} />
    </Stack>
  );
}

// ─── Trial-Based ─────────────────────────────────────────────────────────

export function TrialBasedRecorder({ prompt, onAdd, onUndo, summary, disabled }: {
  prompt: any; onAdd: (p: any) => void; onUndo: () => void;
  summary: { completed: number; correct: number; incorrect: number; prompted: number; noResponse: number; notApplicable: number; percentCorrect: number };
  disabled?: boolean;
}) {
  const btn = (label: string, result: string, color: any) => (
    <Grid item xs={6} md={4}>
      <Button fullWidth size="large" variant="contained" color={color} disabled={disabled}
        onClick={() => onAdd({ entryType: "trial", result, promptLevelId: prompt?.id ?? null, promptLevelName: prompt?.name ?? null })}
        sx={{ py: 2, fontSize: 14 }}>
        {label}
      </Button>
    </Grid>
  );
  return (
    <Box>
      <Grid container spacing={1}>
        {btn(TRIAL_RESULT_LABELS.correct, "correct", "success")}
        {btn(TRIAL_RESULT_LABELS.incorrect, "incorrect", "error")}
        {btn(TRIAL_RESULT_LABELS.prompted, "prompted", "warning")}
        {btn(TRIAL_RESULT_LABELS.no_response, "no_response", "inherit")}
        {btn(TRIAL_RESULT_LABELS.na, "na", "inherit")}
        <Grid item xs={6} md={4}>
          <Button fullWidth size="large" variant="outlined" startIcon={<UndoIcon />} onClick={onUndo} disabled={disabled} sx={{ py: 2 }}>
            Undo Last
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {[
          // Counter labels route through TRIAL_RESULT_LABELS same as
          // the buttons — was "Correct w/ Prompt" while the button
          // above said "Correct with Prompt". One vocabulary.
          ["Trials", summary.completed],
          [TRIAL_RESULT_LABELS.correct, summary.correct],
          [TRIAL_RESULT_LABELS.incorrect, summary.incorrect],
          [TRIAL_RESULT_LABELS.prompted, summary.prompted],
          [TRIAL_RESULT_LABELS.no_response, summary.noResponse],
          [TRIAL_RESULT_LABELS.na, summary.notApplicable],
          ["% Correct", `${summary.percentCorrect}%`],
        ].map(([k, v]) => (
          <Grid item xs={6} md={2} key={String(k)}>
            <Box sx={{ p: 1, textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">{k}</Typography>
              <Typography variant="h6" fontWeight={700}>{v}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Accuracy denominator = correct + incorrect + prompted + no response. N/A excluded.
      </Typography>
    </Box>
  );
}

// ─── Task Analysis ───────────────────────────────────────────────────────

export function TaskAnalysisRecorder({ steps, entries, prompt, onAdd, disabled }: {
  steps: Array<{ id: number | string; name: string }>;
  entries: any[]; prompt: any; onAdd: (p: any) => void; disabled?: boolean;
}) {
  const results = ["independent", "prompted", "incorrect", "not_attempted"];
  const scoreFor = (stepId: any) => entries.filter((e) => e.entryType === "task_step" && String(e.taskStepId) === String(stepId)).slice(-1)[0]?.result;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Whole-task / total-task scoring: press a result for each step. The latest press wins.
      </Typography>
      {steps.length === 0 && <Alert severity="info">No task steps configured on this target.</Alert>}
      <Stack spacing={0.5}>
        {steps.map((s, i) => {
          const current = scoreFor(s.id);
          return (
            <Grid key={s.id} container spacing={1} alignItems="center" sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight={600}>{i + 1}. {s.name}</Typography>
              </Grid>
              {results.map((r) => (
                <Grid item xs={6} md="auto" key={r}>
                  <Button size="small" variant={current === r ? "contained" : "outlined"}
                    disabled={disabled}
                    onClick={() => onAdd({ entryType: "task_step", result: r, taskStepId: s.id, taskStepName: s.name, promptLevelId: prompt?.id ?? null, promptLevelName: prompt?.name ?? null })}>
                    {r.replace("_", " ")}
                  </Button>
                </Grid>
              ))}
            </Grid>
          );
        })}
      </Stack>
    </Box>
  );
}

// ─── Frequency ────────────────────────────────────────────────────────────

export function FrequencyRecorder({ entries, sessionStart, onAdd, onUndo, disabled }: {
  entries: any[]; sessionStart?: number; onAdd: (p: any) => void; onUndo: () => void; disabled?: boolean;
}) {
  const events = entries.filter((e) => e.entryType === "frequency_event");
  const count = events.length;
  const elapsedMin = sessionStart ? Math.max(1 / 60, (Date.now() - sessionStart) / 60000) : null;
  const rate = elapsedMin ? (count / elapsedMin).toFixed(2) : "—";
  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12} md={8}>
          <Button fullWidth variant="contained" color="primary" disabled={disabled}
            onClick={() => onAdd({ entryType: "frequency_event", occurredAt: new Date().toISOString() })}
            sx={{ py: 4, fontSize: 18 }}>+ Add Event</Button>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button fullWidth variant="outlined" startIcon={<UndoIcon />} onClick={onUndo} disabled={disabled} sx={{ py: 4 }}>Undo Last</Button>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Metric label="Count" value={count} />
        <Metric label="Rate / min" value={rate} />
        <Metric label="Elapsed" value={elapsedMin ? `${Math.floor(elapsedMin)}m` : "—"} />
      </Stack>
    </Box>
  );
}

// ─── Duration ─────────────────────────────────────────────────────────────

export function DurationRecorder({ entries, onAdd, disabled }: {
  entries: any[]; onAdd: (p: any) => void; disabled?: boolean;
}) {
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    if (running) timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Persist active timer state locally so a refresh doesn't lose the episode context.
  useEffect(() => {
    const raw = localStorage.getItem("stepup.session.durationTimer");
    if (raw) {
      try {
        const s = JSON.parse(raw);
        if (s.running && s.startedAt) {
          setRunning(true); setStartedAt(s.startedAt);
          setElapsed(Math.floor((Date.now() - s.startedAt) / 1000));
        }
      } catch {}
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("stepup.session.durationTimer", JSON.stringify({ running, startedAt }));
  }, [running, startedAt]);

  const start = () => { setRunning(true); setStartedAt(Date.now()); setElapsed(0); };
  const pause = () => setRunning(false);
  const resume = () => setRunning(true);
  const stop = () => {
    setRunning(false);
    if (startedAt) {
      onAdd({ entryType: "duration_episode", durationSeconds: elapsed, occurredAt: new Date(startedAt).toISOString() });
    }
    setStartedAt(null); setElapsed(0);
  };

  const eps = entries.filter((e) => e.entryType === "duration_episode");
  const secs = eps.map((e) => Number(e.durationSeconds) || 0);
  const total = secs.reduce((a, b) => a + b, 0);
  const avg = secs.length ? Math.round(total / secs.length) : 0;
  const longest = secs.length ? Math.max(...secs) : 0;

  return (
    <Box>
      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, textAlign: "center", mb: 2 }}>
        <Typography variant="h3" fontWeight={700}>{fmt(elapsed)}</Typography>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          {!running && !startedAt && <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={start} disabled={disabled}>Start</Button>}
          {running && <Button variant="outlined" startIcon={<PauseIcon />} onClick={pause} disabled={disabled}>Pause</Button>}
          {!running && startedAt && <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={resume} disabled={disabled}>Resume</Button>}
          {startedAt && <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={stop} disabled={disabled}>Stop & Save</Button>}
        </Stack>
      </Box>
      <Stack direction="row" spacing={2}>
        <Metric label="Episodes" value={eps.length} />
        <Metric label="Total" value={fmt(total)} />
        <Metric label="Average" value={fmt(avg)} />
        <Metric label="Longest" value={fmt(longest)} />
      </Stack>
    </Box>
  );
}

// ─── Latency ──────────────────────────────────────────────────────────────

export function LatencyRecorder({ entries, prompt, onAdd, disabled }: {
  entries: any[]; prompt: any; onAdd: (p: any) => void; disabled?: boolean;
}) {
  const [instrAt, setInstrAt] = useState<number | null>(null);
  const present = () => setInstrAt(Date.now());
  const response = (result: string) => {
    if (!instrAt) return;
    const ms = Date.now() - instrAt;
    onAdd({ entryType: "latency_attempt", latencyMilliseconds: ms, result, promptLevelId: prompt?.id ?? null, promptLevelName: prompt?.name ?? null });
    setInstrAt(null);
  };
  const atts = entries.filter((e) => e.entryType === "latency_attempt");
  const ms = atts.map((e) => Number(e.latencyMilliseconds) || 0);
  const avg = ms.length ? Math.round(ms.reduce((a, b) => a + b, 0) / ms.length) : 0;
  const fastest = ms.length ? Math.min(...ms) : 0;
  const longest = ms.length ? Math.max(...ms) : 0;
  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Button fullWidth size="large" variant="contained" onClick={present} disabled={disabled || !!instrAt} sx={{ py: 2 }}>
            {instrAt ? "Waiting for response…" : "Present Instruction"}
          </Button>
        </Grid>
        <Grid item xs={6} md={2}><Button fullWidth variant="contained" color="success" onClick={() => response("correct")} disabled={disabled || !instrAt}>Correct</Button></Grid>
        <Grid item xs={6} md={2}><Button fullWidth variant="contained" color="warning" onClick={() => response("prompted")} disabled={disabled || !instrAt}>Prompted</Button></Grid>
        <Grid item xs={12} md={2}><Button fullWidth variant="outlined" onClick={() => setInstrAt(null)} disabled={disabled || !instrAt}>Cancel</Button></Grid>
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Metric label="Attempts" value={atts.length} />
        <Metric label="Avg" value={`${avg}ms`} />
        <Metric label="Fastest" value={`${fastest}ms`} />
        <Metric label="Longest" value={`${longest}ms`} />
      </Stack>
    </Box>
  );
}

// ─── Interval ────────────────────────────────────────────────────────────

export function IntervalRecorder({ entries, onAdd, disabled }: {
  entries: any[]; onAdd: (p: any) => void; disabled?: boolean;
}) {
  const [intervalSeconds, setIntervalSeconds] = useState(15);
  const [count, setCount] = useState(20);
  const [mode, setMode] = useState("whole");
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(0);
  const [remaining, setRemaining] = useState(intervalSeconds);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          setCurrent((c) => Math.min(count, c + 1));
          return intervalSeconds;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, intervalSeconds, count]);

  const record = (result: string) => {
    if (current >= count) return;
    onAdd({ entryType: "interval", intervalIndex: current, result, note: mode });
  };

  const iv = entries.filter((e) => e.entryType === "interval");
  const total = iv.length;
  const present = iv.filter((e) => e.result === "present").length;
  const prompted = iv.filter((e) => e.result === "prompted").length;

  return (
    <Box>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={6} md={3}><TextField label="Interval (sec)" size="small" type="number" fullWidth value={intervalSeconds} onChange={(e) => setIntervalSeconds(Math.max(1, Number(e.target.value)))} disabled={running} /></Grid>
        <Grid item xs={6} md={3}><TextField label="# Intervals" size="small" type="number" fullWidth value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value)))} disabled={running} /></Grid>
        <Grid item xs={6} md={3}>
          <Select fullWidth size="small" value={mode} onChange={(e) => setMode(e.target.value)} disabled={running}>
            <MenuItem value="whole">Whole interval</MenuItem>
            <MenuItem value="partial">Partial interval</MenuItem>
            <MenuItem value="momentary">Momentary sampling</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={6} md={3}>
          {!running
            ? <Button fullWidth variant="contained" onClick={() => { setRunning(true); setRemaining(intervalSeconds); }} disabled={disabled}>Start</Button>
            : <Button fullWidth variant="outlined" onClick={() => setRunning(false)} disabled={disabled}>Pause</Button>}
        </Grid>
      </Grid>
      <Box sx={{ p: 2, textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Interval {current + 1} / {count}</Typography>
        <Typography variant="h3" fontWeight={700}>{remaining}s</Typography>
      </Box>
      <Grid container spacing={1}>
        {["present", "absent", "prompted", "skip"].map((r) => (
          <Grid item xs={6} md={3} key={r}>
            <Button fullWidth variant="contained" onClick={() => record(r)} disabled={disabled}>
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </Button>
          </Grid>
        ))}
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Metric label="Recorded" value={total} />
        <Metric label="% Present" value={total ? `${Math.round((present / total) * 100)}%` : "—"} />
        <Metric label="% Prompted" value={total ? `${Math.round((prompted / total) * 100)}%` : "—"} />
      </Stack>
    </Box>
  );
}

// ─── ABC ─────────────────────────────────────────────────────────────────

const emptyAbc = () => ({
  settingEvent: "", antecedent: "", behaviour: "", consequence: "", intensity: "",
  durationSeconds: "", locationNote: "", peoplePresent: "", followUpAction: "", note: "",
});

export function AbcBehaviourRecorder({ entries, onAdd, onDelete, disabled }: {
  entries: any[]; onAdd: (p: any) => void; onDelete: (id: number) => void; disabled?: boolean;
}) {
  const [form, setForm] = useState<any>(emptyAbc());
  const save = (dup = false) => {
    if (!form.behaviour.trim()) return;
    onAdd({
      entryType: "abc", occurredAt: new Date().toISOString(),
      settingEvent: form.settingEvent || null, antecedent: form.antecedent || null,
      behaviour: form.behaviour, consequence: form.consequence || null,
      intensity: form.intensity || null,
      durationSeconds: form.durationSeconds ? Number(form.durationSeconds) : null,
      locationNote: form.locationNote || null, peoplePresent: form.peoplePresent || null,
      followUpAction: form.followUpAction || null, note: form.note || null,
    });
    if (!dup) setForm(emptyAbc());
  };
  const events = entries.filter((e) => e.entryType === "abc");
  const fld = (k: string, label: string, cols = 4, multiline = false) => (
    <Grid item xs={12} md={cols}>
      <TextField label={label} size="small" fullWidth multiline={multiline} minRows={multiline ? 2 : undefined}
        value={form[k]} onChange={(e) => setForm((f: any) => ({ ...f, [k]: e.target.value }))} disabled={disabled} />
    </Grid>
  );
  return (
    <Box>
      <Grid container spacing={1}>
        {fld("settingEvent", "Setting Event")}
        {fld("antecedent", "Antecedent")}
        {fld("behaviour", "Behaviour *")}
        {fld("consequence", "Consequence")}
        {fld("intensity", "Intensity")}
        {fld("durationSeconds", "Duration (sec)")}
        {fld("locationNote", "Location")}
        {fld("peoplePresent", "People Present")}
        {fld("followUpAction", "Follow-up Action")}
        {fld("note", "Notes", 12, true)}
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => save(false)} disabled={disabled || !form.behaviour.trim()}>Save Event</Button>
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => save(true)} disabled={disabled || !form.behaviour.trim()}>Duplicate</Button>
      </Stack>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1 }}>ABC Timeline ({events.length})</Typography>
      <Stack spacing={0.5}>
        {events.map((e) => (
          <Box key={e.id} sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Typography variant="caption" color="text.secondary">{new Date(e.occurredAt).toLocaleTimeString()}</Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={600}>{e.behaviour}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[e.antecedent && `A: ${e.antecedent}`, e.consequence && `C: ${e.consequence}`, e.intensity, e.durationSeconds && `${e.durationSeconds}s`].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            {!disabled && <IconButton size="small" onClick={() => onDelete(e.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────

function Metric({ label, value }: { label: string; value: any }) {
  return (
    <Box sx={{ p: 1, textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1, flexGrow: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700}>{value}</Typography>
    </Box>
  );
}

function fmt(s: number): string {
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}
